import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './UserCritiria.css';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';

const UserCritiria = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [userId, setUserId] = useState(null);
  const [showCustomOccupationInput, setShowCustomOccupationInput] = useState(false);
  const [formData, setFormData] = useState({
    field: '',
    occupation: [],
    skills: [],
    certifications: [],
  });

  const { fetchUserProfile } = useAuth();

  const fieldsOfLearning = [
    'Software Development', 'Human Resources', 'Data & Analytics', 'Education & Training',
    'Information Technology', 'Customer Support', 'Marketing', 'Health & Wellness',
    'Design', 'Writing', 'Finance & Accounting', 'Legal',
    'Product & Project Management', 'Art', 'Business Operations', 'None of the above',
    'Sales & Business Development',
  ];

  const occupations = {
    'Data & Analytics': [
      'Business Analyst', 'Financial Analyst', 'Business Intelligence Analyst', 'GIS Analyst',
      'Data Analyst', 'Machine Learning Engineer', 'Data Engineer', 'Marketing Analyst',
      'Data Scientist',
    ],
    'Software Development': [
      'Android Developer', 'Mobile Application Developer', 'Back End Web Developer', '.NET Developer',
      'Data Engineer', 'PHP Developer', 'DevOps Engineer', 'Python Developer',
      'Front End Web Developer', 'Software Architect', 'Full Stack Web Developer', 'Software Developer',
      'Game Developer', 'Software Quality Assurance Engineer', 'iOS Developer', 'Software Tester',
      'Java Developer', 'Web Developer', 'JavaScript Developer', 'WordPress Developer',
      'Machine Learning Engineer',
    ],
    'Information Technology': [
      'Cloud Engineer', 'Network Engineer', 'Cybersecurity Analyst', 'System Administrator',
      'Cybersecurity Engineer', 'Systems Engineer', 'Ethical Hacker', 'Technical Program Manager',
      'IT Support Specialist', 'Technical Writer', 'Network Administrator',
    ],
    'Marketing': [
      'Affiliate Marketer', 'Marketer', 'Blogger', 'Marketing Analyst',
      'Brand Manager', 'Product Marketing Manager', 'Content Marketer', 'SEO Specialist',
      'Digital Marketer', 'Social Media Influencer', 'Email Marketer', 'Social Media Marketer',
      'Growth Marketer',
    ],
    'Design': [
      '3D Artist', 'Instructional Designer', 'Animator', 'Interior Designer',
      'Architect (buildings)', 'Motion Graphic Designer', 'Fashion Designer', 'UX Designer',
      'Game Designer', 'UX Researcher', 'Graphic Designer', 'Web Designer',
    ],
    'Finance & Accounting': [
      'Accountant', 'Financial Auditor', 'Financial Analyst', 'Financial Trader',
    ],
    'Product & Project Management': [
      'Product Manager', 'Project Manager', 'Product Marketing Manager', 'Technical Program Manager',
    ],
    'Business Operations': [
      'Administrative Professional', 'Supply Chain Manager',
    ],
    'Sales & Business Development': [
      'Account Manager', 'Sales Representative', 'Business Development Manager',
    ],
    'Human Resources': [
      'HR Manager', 'HR Recruiter',
    ],
    'Education & Training': [
      'Educator', 'Instructional Designer',
    ],
    'Customer Support': [
      'Customer Success Manager', 'Customer Support Advisor',
    ],
    'Health & Wellness': [
      'Fitness Trainer', 'Mental Health Practitioner', 'Life Coach', 'Nurse',
      'Medical Doctor', 'Nutritionist',
    ],
    'Writing': [
      'Blogger', 'Writer', 'Technical Writer',
    ],
    'Legal': [
      'Lawyer',
    ],
    'Art': [
      'Actor', 'Photographer', 'Filmmaker', 'Visual Artist', 'Musician',
    ],
    // Add more occupations based on field if needed, or keep it generic
  };

  const popularSkills = {
    'Software': ['MERN Stack', 'ReactJS', 'MongoDB', 'Node.Js', 'Microsoft Power BI', 'Microsoft Excel', 'Tableau'],
    'Programming Languages': ['Python', 'JavaScript', 'Java', 'C++', 'C#', 'Go', 'Swift', 'Kotlin', 'TypeScript', 'Ruby'],
    'Web Development': ['HTML', 'CSS', 'Next.js', 'Angular', 'Vue.js', 'Django', 'Flask', 'Spring Boot', 'ASP.NET'],
    'Data Science': ['Machine Learning', 'Deep Learning', 'Pandas', 'NumPy', 'Scikit-learn', 'TensorFlow', 'Keras', 'PyTorch', 'R'],
    'Cloud Computing': ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes'],
    'Mobile Development': ['React Native', 'Flutter', 'Android Development', 'iOS Development'],
    'Cybersecurity': ['Ethical Hacking', 'Network Security', 'Penetration Testing', 'Security Analytics'],
    'Design': ['UI/UX Design', 'Figma', 'Adobe XD', 'Photoshop', 'Illustrator'],
    'Marketing': ['Digital Marketing', 'SEO', 'Content Marketing', 'Social Media Marketing', 'Email Marketing'],
    'Project Management': ['Agile', 'Scrum', 'PMP', 'Jira', 'Asana'],
    'Business': ['Financial Modeling', 'Business Strategy', 'Entrepreneurship', 'Sales', 'Negotiation'],
    'Human Resources': ['Recruitment', 'Talent Management', 'HR Analytics', 'Compensation & Benefits'],
    'Education': ['Instructional Design', 'Curriculum Development', 'E-learning'],
    'Writing': ['Copywriting', 'Technical Writing', 'Creative Writing', 'Grant Writing'],
    'Finance & Accounting': ['Bookkeeping', 'Financial Planning', 'Tax Preparation', 'Auditing'],
    'Legal': ['Legal Research', 'Contract Law', 'Intellectual Property'],
    'Art': ['Drawing', 'Painting', 'Sculpting', 'Digital Art'],
  };

  const popularCertifications = {
    'Business Analysis': ['IIBA Certified Business Analysis Professional (CBAP)', 'IIBA Entry Certificate in Business Analysis (ECBA)', 'IIBA Certification of Capability in Business Analysis (CCBA)'],
    // Add more certifications based on category
  };

  useEffect(() => {
    const storedUserId = localStorage.getItem('tempUserId');
    if (storedUserId) {
      setUserId(storedUserId);
    } else {
      // If no userId, redirect to signup or login
      navigate('/signup');
      toast.error('Please sign up or log in to complete your profile.');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'field') {
      setFormData((prevData) => ({
        ...prevData,
        occupation: [],
      }));
      setShowCustomOccupationInput(false);
    }
  };

  const handleOccupationChange = (selectedOccupation) => {
    setFormData((prevData) => {
      const currentOccupations = prevData.occupation;
      if (currentOccupations.includes(selectedOccupation)) {
        return { ...prevData, occupation: currentOccupations.filter((occ) => occ !== selectedOccupation) };
      } else {
        return { ...prevData, occupation: [...currentOccupations, selectedOccupation] };
      }
    });
    setShowCustomOccupationInput(false);
  };

  const handleCustomOccupationChange = (e) => {
    setFormData({ ...formData, occupation: [e.target.value] });
  };

  const handleCustomOccupationKeyDown = (e) => {
    if (e.key === 'Enter' && formData.occupation.length > 0) {
      e.preventDefault();
      handleNext();
    }
  };

  const handleCantFindOccupation = () => {
    setShowCustomOccupationInput(true);
    setFormData((prevData) => ({ ...prevData, occupation: '' }));
  };

  const handleSkillsChange = (skill) => {
    setFormData((prevData) => {
      const currentSkills = prevData.skills;
      if (currentSkills.includes(skill)) {
        return { ...prevData, skills: currentSkills.filter((s) => s !== skill) };
      } else {
        return { ...prevData, skills: [...currentSkills, skill] };
      }
    });
  };

  const handleCertificationChange = (certification) => {
    setFormData((prevData) => {
      const currentCertifications = prevData.certifications;
      if (currentCertifications.includes(certification)) {
        return { ...prevData, certifications: currentCertifications.filter((c) => c !== certification) };
      } else {
        return { ...prevData, certifications: [...currentCertifications, certification] };
      }
    });
  };

  const handleNext = () => {
    if (step === 1 && !formData.field) {
      toast.error('Please select a field of learning.');
      return;
    }
    if (step === 2) {
      if (showCustomOccupationInput) {
        if (formData.occupation.trim() === '') {
          toast.error('Please enter your occupation.');
          return;
        }
      } else if (formData.occupation.length === 0) {
        toast.error('Please select at least one occupation or enter your own.');
        return;
      }
    }
    if (step === 3 && formData.skills.length === 0) {
      toast.error('Please select at least one skill.');
      return;
    }
    setStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    const currentUserId = localStorage.getItem('tempUserId');

    if (!currentUserId) {
      toast.error('User ID not found. Please sign up again.');
      navigate('/signup');
      return;
    }

    try {
      const response = await axios.post(api.submitUserCriteria, {
        userId: currentUserId,
        ...formData,
      });

      if (response.data.success) {
        toast.success('Profile updated successfully!');
        localStorage.removeItem('tempUserId');

        // Wait for profile update before navigating
        await fetchUserProfile(localStorage.getItem('authToken'));
        navigate('/home');
      } else {
        toast.error(response.data.message || 'Failed to update profile.');
      }
    } catch (error) {
      console.error('Error submitting user criteria:', error);
      toast.error('An error occurred while saving your preferences.');
    }
  };

  const renderStep = () => {
    switch (step) {
    case 1:
      return (
        <div className="user-criteria-step">
          <h3 className="user-criteria-heading">What field are you learning for?</h3>
          <div className="user-criteria-options-grid">
            {fieldsOfLearning.map((field) => (
              <label key={field} className="user-criteria-radio-label">
                <input
                  type="radio"
                  name="field"
                  value={field}
                  checked={formData.field === field}
                  onChange={handleChange}
                  className="user-criteria-radio-input"
                />
                {field}
              </label>
            ))}
          </div>
          <div className="user-criteria-button-container">
            <button type="button" className="user-criteria-next-button" onClick={handleNext}>
                Next
            </button>
          </div>
        </div>
      );
    case 2:
      return (
        <div className="user-criteria-step">
          <h3 className="user-criteria-heading">Which occupation are you learning for?</h3>
          <div className="user-criteria-occupation-section">
            {formData.field && occupations[formData.field] ? (
              <>
                <h4 className="user-criteria-subheading">{formData.field} occupations</h4>
                <div className="user-criteria-options-grid">
                  {occupations[formData.field].map((occupation) => (
                    <label key={occupation} className="user-criteria-radio-label">
                      <input
                        type="checkbox"
                        name="occupation"
                        value={occupation}
                        checked={formData.occupation.includes(occupation) && !showCustomOccupationInput}
                        onChange={() => handleOccupationChange(occupation)}
                        className="user-criteria-radio-input"
                        disabled={showCustomOccupationInput}
                      />
                      {occupation}
                    </label>
                  ))}
                </div>
              </>
            ) : (
              <p className="user-criteria-placeholder-text">Select a field to see occupations</p>
            )}
            {!showCustomOccupationInput && (
              <a href="#" className="user-criteria-link" onClick={handleCantFindOccupation}>
                  I can't find my occupation
              </a>
            )}
            {showCustomOccupationInput && (
              <div className="user-criteria-custom-input-container">
                <h4 className="user-criteria-subheading">We'll do our best to bring you relevant recommendations.</h4>
                <p className="user-criteria-custom-input-label">My occupation</p>
                <input
                  type="text"
                  placeholder="Enter title"
                  value={formData.occupation}
                  onChange={handleCustomOccupationChange}
                  onKeyDown={handleCustomOccupationKeyDown}
                  className="user-criteria-custom-occupation-input"
                />
              </div>
            )}
          </div>
          <div className="user-criteria-button-container">
            <button type="button" className="user-criteria-back-button" onClick={handleBack}>
                Back
            </button>
            <button type="button" className="user-criteria-next-button" onClick={handleNext}>
                Next
            </button>
          </div>
        </div>
      );
    case 3:
      return (
        <div className="user-criteria-step">
          <h3 className="user-criteria-heading">What skills are you interested in?</h3>
          <p className="user-criteria-sub-text">Choose a few to start with. You can change these or follow more skills in the future.</p>
          <div className="user-criteria-selected-skills-container">
            {formData.skills.map((skill) => (
              <span key={skill} className="user-criteria-selected-skill-tag">
                {skill} <span className="user-criteria-remove-skill" onClick={() => handleSkillsChange(skill)}>x</span>
              </span>
            ))}
            <div className="user-criteria-search-box">
              <input
                type="text"
                placeholder="Search for a skill"
                className="user-criteria-search-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission
                  }
                }}
              />
            </div>
          </div>
          <div className="user-criteria-popular-section">
            <h4 className="user-criteria-subheading">Popular with learners like you</h4>
            {Object.entries(popularSkills).map(([category, skills]) => (
              <div key={category} className="user-criteria-skill-category">
                <h5 className="user-criteria-category-title">{category}</h5>
                <div className="user-criteria-skill-tags-container">
                  {skills.map((skill) => (
                    <span
                      key={skill}
                      className={`user-criteria-skill-tag ${formData.skills.includes(skill) ? 'selected' : ''}`}
                      onClick={() => handleSkillsChange(skill)}
                    >
                        + {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="user-criteria-button-container">
            <button type="button" className="user-criteria-back-button" onClick={handleBack}>
                Back
            </button>
            <button type="button" className="user-criteria-next-button" onClick={handleNext}>
                Next
            </button>
          </div>
        </div>
      );
    case 4:
      return (
        <div className="user-criteria-step">
          <h3 className="user-criteria-heading">Are you interested in any certifications?</h3>
          <div className="user-criteria-search-box">
            <input
              type="text"
              placeholder="Search for a certification"
              className="user-criteria-search-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault(); // Prevent form submission
                }
              }}
            />
          </div>
          <div className="user-criteria-popular-section">
            <h4 className="user-criteria-subheading">Popular with learners like you</h4>
            {Object.entries(popularCertifications).map(([category, certifications]) => (
              <div key={category} className="user-criteria-certification-category">
                <h5 className="user-criteria-category-title">{category}</h5>
                {certifications.map((certification) => (
                  <label key={certification} className="user-criteria-checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.certifications.includes(certification)}
                      onChange={() => handleCertificationChange(certification)}
                      className="user-criteria-checkbox-input"
                    />
                    {certification}
                  </label>
                ))}
              </div>
            ))}
          </div>
          <div className="user-criteria-button-container">
            <button type="button" className="user-criteria-back-button" onClick={handleBack}>
                Back
            </button>
            <button type="button" className="user-criteria-submit-button" onClick={handleSubmit}>
                Submit
            </button>
          </div>
        </div>
      );
    default:
      return null;
    }
  };

  const pageTitles = [
    'Answer a few questions to improve your content recommendations', // General header for the first step
    'What field are you learning for?',
    'Which occupation are you learning for?',
    'What skills are you interested in?',
    'Are you interested in any certifications?',
  ];

  return (
    <div className="user-criteria-container">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="user-criteria-header-card">
        <p>You're in the right place!</p>
        {step === 2 && formData.occupation.length > 0 && typeof formData.occupation === 'object' && (
          <p className="user-criteria-header-text">1,304,996 people learn {formData.occupation.join(', ')} on UlsaApp</p>
        )}
      </div>
      <div className="user-criteria-form-card">
        {renderStep()}
      </div>
    </div>
  );
};

export default UserCritiria;
